import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CommercialScheduleServices } from "../../../core/services/CommercialScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import {
  CommercialScheduleFields,
  CommercialScheduleListParams,
  Permission,
} from "./CommercialScheduleDataTypes";
import "./CommercialSchedules.css";
import CommercialScheduleFilters from "./CommercialScheduleFilters";
import {
  countCommaSeparatedVals,
  getTodayShortDay,
  isEmptyVal,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CommercialScheduleSingleAction from "./CommercialScheduleSingleAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  scheduleForTime,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import CommercialScheduleItems from "./CommercialScheduleItems";
import CommercialScheduleInfoCell from "../../components/Elements/CommercialScheduleInfoCell";
import CommercialScheduleStoresModal from "./CommercialScheduleStoresModal";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function CommercialSchedules() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CommercialScheduleFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [itemSearchParams, setItemSearchParams] = useState<any>({
    smart_search: "",
    play_for_day: getTodayShortDay().toLowerCase(),
    start_hour: "",
  });
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedCommercialSchedule, setSelectedCommercialSchedule] =
    useState<any>({});
  const [openModalByName, setOpenModalByName] = useState("");
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const loggedInUserDetail = JSON.parse(
    localStorage.getItem("loggedInUserDetail") || "{}"
  );
  const [tabView, setTabView] = useState(scheduleForTime.current.value);

  const initSearchFormData = {
    name: "",
    company: "",
    store: "",
    new_schedule_everyday: "",
    start_date: null,
    end_date: null,
    schedule_date: null,
    period_nature: "",
    item_count: "",
    item_search: "",
    start_at: "",
    end_at: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<CommercialScheduleListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const [commercialScheduleFetched, setCommercialScheduleFetched] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  const [failedImages, setFailedImages] = useState<any>([]);
  const publishPermission = UtilityServices.hasPermission(
    "publish-commercial-schedule"
  );
  const updatePermission = UtilityServices.hasPermission(
    "update-commercial-schedule"
  );
  const deletePermission = UtilityServices.hasPermission(
    "delete-commercial-schedule"
  );
  const permissionObj: Permission = {
    publish: publishPermission,
    update: updatePermission,
    delete: deletePermission,
  };

  function getParamsFromTab(tabView: string) {
    let tParams: any = {};
    switch (tabView) {
      case scheduleForTime.current.value:
        tParams.period_nature = scheduleForTime.current.value;
        break;
      case scheduleForTime.upcoming.value:
        tParams.period_nature = scheduleForTime.upcoming.value;
        break;
      case scheduleForTime.past.value:
        tParams.period_nature = scheduleForTime.past.value;
        break;
      case scheduleForTime.all.value:
        break;
    }
    return tParams;
  }

  function selectCommercialSchedule(
    resData: any,
    selectCommercialScheduleId: number = 0
  ) {
    setSelectedCommercialSchedule({});
    if (resData.length > 0) {
      if (selectCommercialScheduleId == 0) {
        setSelectedCommercialSchedule(resData[0]);
      } else {
        let commercialScheduleToSelect = data
          .filter((item) => item.id == selectCommercialScheduleId)
          .shift();
        if (commercialScheduleToSelect) {
          setSelectedCommercialSchedule(commercialScheduleToSelect);
        } else {
          setSelectedCommercialSchedule(resData[0]);
        }
      }
    }
  }

  const handleScheduleStores = (scheduleId: any) => {
    setOpenModalByName("view-commercial-schedule-stores");
    selectCommercialSchedule(data, scheduleId);
  };

  async function getCommercialSchedules(
    params: CommercialScheduleListParams,
    selectCommercialScheduleId: number = 0
  ) {
    try {
      let compId: number = getCompanyId();
      if (!compId) {
        setData([]);
        setCommercialScheduleFetched(false);
        return;
      }

      setLoading(true);
      setCommercialScheduleFetched(false);

      params = {
        ...params,
        ...getParamsFromTab(tabView),
        ...{ company: compId },
      };
      const res = await CommercialScheduleServices.getCommercialSchedules(
        params
      );
      const resData = get("data.data", res);
      setData(resData);
      setTotal(get("data.total", res));
      selectCommercialSchedule(resData, selectCommercialScheduleId);
      setCheckedKeys([]);
      setLoading(false);
      setCommercialScheduleFetched(true);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      setShowCreate(
        UtilityServices.hasPermission("create-commercial-schedule")
      );
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function deleteCommercialSchedule(commercialSchedule: any) {
    try {
      let res = await CommercialScheduleServices.deleteCommercialSchedule(
        commercialSchedule?.id
      );
      setPage(1);
      getCommercialSchedules(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function fetchCommercialScheduleItems(commercialSchedule: any) {
    setSelectedCommercialSchedule(commercialSchedule);
  }

  async function publishCommercialSchedule(commercialSchedule: any) {
    try {
      let status =
        commercialSchedule?.status == statusArr.inactive
          ? statusArr.active
          : statusArr.inactive;
      let res = await CommercialScheduleServices.publishCommercialSchedule(
        commercialSchedule?.id,
        {
          status,
        }
      );
      setPage(1);
      getCommercialSchedules(
        getSearchParams({
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...data,
    };

    let params: CommercialScheduleListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }

    return params;
  }

  function processCreateSchedule() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    navigate(ContentRoutes.CREATE_COMMERCIAL_SCHEDULE);
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onPageChange(page: number) {
    setPage(page);
    getCommercialSchedules(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCommercialSchedules(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getCommercialSchedules(getSearchParams(formData));
  }

  function submitSearchAfterStoreChange(prevCommercialScheduleId: any) {
    setPage(page);
    getCommercialSchedules(getSearchParams(formData), prevCommercialScheduleId);
  }

  function submitSearchAfterItemChange(
    prevCommercialScheduleId: any,
    itmSearchParams?: any
  ) {
    setPage(page);
    getCommercialSchedules(getSearchParams(formData), prevCommercialScheduleId);
    itmSearchParams && setItemSearchParams(itmSearchParams);
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCommercialSchedules(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterCommercialSchedules = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCommercialSchedules(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
        })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getCommercialSchedules(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getCommercialSchedules(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };

  const [showColumns, setShowColumns] = useState([
    { fieldName: t("IMAGE") },
    { fieldName: t("NAME") },
  ]);

  useEffect(() => {
    getCommercialSchedules(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
    switch (tabView) {
      case scheduleForTime.current.value:
        setShowColumns([{ fieldName: t("NAME") }, { fieldName: t("STORES") }]);
        break;
      case scheduleForTime.upcoming.value:
        setShowColumns([{ fieldName: t("NAME") }, { fieldName: t("STORES") }]);
        break;
      case scheduleForTime.past.value:
        setShowColumns([{ fieldName: t("NAME") }, { fieldName: t("STORES") }]);
        break;
      case scheduleForTime.all.value:
        setShowColumns([{ fieldName: t("NAME") }, { fieldName: t("STORES") }]);
        break;
      default:
        break;
    }
  }, [tabView, company]);

  return (
    <div className="commercial-schedules customers">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("COMMERCIAL_SCHEDULES")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateSchedule}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content className="commercial-schedules-content">
          <CommercialScheduleFilters
            tabViewSelected={tabView}
            onSubmit={submitSearch}
            onClear={clearSearch}
          />
          <div className="commercial-schedules-split-layout">
            <div
              className={
                total > 0
                  ? "commercial-schedule-list-panel usr-tbl-border"
                  : "commercial-schedule-list-panel no-record-table"
              }
            >
              <div className="row commercial-schedule-list-search-row">
                <div className="col-md-12">
                  <InputGroup className="commercial-schedule-list-smart-search">
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("COMMERCIAL_SCHEDULE").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCommercialSchedules(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCommercialSchedules(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div className="commercial-schedule-tabs">
                <button
                  style={{
                    opacity:
                      tabView === scheduleForTime.current.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.current.value)}
                >
                  {scheduleForTime.current.label}
                </button>
                <button
                  style={{
                    opacity:
                      tabView === scheduleForTime.upcoming.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.upcoming.value)}
                >
                  {scheduleForTime.upcoming.label}
                </button>
                <button
                  style={{
                    opacity: tabView === scheduleForTime.past.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.past.value)}
                >
                  {scheduleForTime.past.label}
                </button>
                <button
                  style={{
                    opacity: tabView === scheduleForTime.all.value ? 1 : 0.5,
                  }}
                  className="tab-button"
                  onClick={(e) => setTabView(scheduleForTime.all.value)}
                >
                  {scheduleForTime.all.label}
                </button>
              </div>

              <div className="commercial-schedule-table">
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onRowClick={fetchCommercialScheduleItems}
                  rowClassName={(rowData: any) =>
                    rowData?.id === selectedCommercialSchedule?.id
                      ? "commercial-schedule-row-selected"
                      : ""
                  }
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  rowHeight={80}
                  hover={true}
                  renderEmpty={() => (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      {getCompanyId() !== null ? (
                        <>
                          <h4>
                            {t("NO_ENTITY_FOUND", {
                              entity: t("COMMERCIAL_SCHEDULES"),
                            })}
                          </h4>
                          <p>{t("ADJUST_FILTER")}</p>
                        </>
                      ) : (
                        <h4>
                          {t("PLEASE_SELECT_FIELD", { field: t("COMPANY") })}
                        </h4>
                      )}
                    </div>
                  )}
                >
                  {showColumns?.length &&
                    showColumns.map((field: any, index: number) => {
                      return (
                        <>
                          {field.fieldName === t("NAME") ? (
                            <Column
                              width={220}
                              sortable
                              resizable
                              flexGrow={1}
                              fixed
                              key={index}
                            >
                              <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                              <CommercialScheduleInfoCell
                                dataKey="schedule_title"
                                rowData={(rowData: any) => rowData}
                                onClick={fetchCommercialScheduleItems}
                              />
                            </Column>
                          ) : null}

                          {field.fieldName === t("STORES") ? (
                            <Column width={120} resizable key={index}>
                              <CustomHeaderCell>{t("STORES")}</CustomHeaderCell>
                              <CustomCell dataKey="store_ids">
                                {(rowData: any) => (
                                  <a
                                    onClick={() => {
                                      handleScheduleStores(rowData?.id);
                                    }}
                                  >
                                    {countCommaSeparatedVals(rowData.store_ids)}
                                  </a>
                                )}
                              </CustomCell>
                            </Column>
                          ) : null}
                        </>
                      );
                    })}

                  <Column width={60} fixed="right">
                    <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                    <Cell>
                      {(rowData) => (
                        <CommercialScheduleSingleAction
                          commercialScheduleData={rowData}
                          onDeleteCommercialSchedule={deleteCommercialSchedule}
                          onPublishCommercialSchedule={
                            publishCommercialSchedule
                          }
                          permission={permissionObj}
                        />
                      )}
                    </Cell>
                  </Column>
                </Table>
              </div>
              <Footer className="footer">
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  size="xs"
                  layout={["total", "-", "|", "pager"]}
                  total={total}
                  limitOptions={appConstObj.limit_options}
                  limit={rows}
                  activePage={page}
                  onChangePage={onPageChange}
                  onChangeLimit={handleChangeRows}
                />
              </Footer>
            </div>
            <div className="commercial-schedule-book-separator" aria-hidden="true" />
            <div className="commercial-schedule-items-panel">
              {commercialScheduleFetched && (
                <CommercialScheduleItems
                  commercialScheduleId={selectedCommercialSchedule?.id}
                  onDeleteItems={submitSearchAfterItemChange}
                  itemSearchParams={itemSearchParams}
                />
              )}
            </div>

            <ToastContainer />
          </div>
        </Content>
      </Container>
      {openModalByName === "view-commercial-schedule-stores" && (
        <CommercialScheduleStoresModal
          commercialScheduleId={selectedCommercialSchedule?.id}
          commercialScheduleData={selectedCommercialSchedule}
          openModal={
            openModalByName === "view-commercial-schedule-stores" ? true : false
          }
          setOpenModal={setOpenModalByName}
          onDeleteStores={submitSearchAfterStoreChange}
        />
      )}
    </div>
  );
}

export default CommercialSchedules;
